/**
 * /hospitality/ — the Hospitality back-office command center landing page.
 *
 * One screen that rolls up the property's nightly posture: headline KPIs
 * pulled live from the three operational summaries (night audit, Title 31,
 * gaming tax), then a grid of module cards linking to each workflow.
 *
 * Read-only: this page only reads the same endpoints the detail pages use;
 * it issues no writes and adds no new contract.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, StatusPill, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/")({
  component: HospitalityOverviewPage,
});

// ── Minimal inline shapes — just the fields this page reads ─────────────────
interface NightAuditSummary {
  drj: { total_credit: string; balanced: boolean } | null;
  net_over_short: string;
  message?: string;
}
interface Title31Summary {
  ctrs: unknown[];
  sars: unknown[];
  message?: string;
}
interface GamingTaxSummary {
  today_accrual: string | null;
  marginal_rate: string | null;
  message?: string;
}

function HospitalityOverviewPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const naQ = useQuery({
    queryKey: ["hospitality-night-audit"],
    queryFn: () => api<NightAuditSummary>("/api/hospitality/night-audit"),
  });
  const t31Q = useQuery({
    queryKey: ["hospitality-title-31"],
    queryFn: () => api<Title31Summary>("/api/hospitality/title-31"),
  });
  const gtQ = useQuery({
    queryKey: ["hospitality-gaming-tax"],
    queryFn: () => api<GamingTaxSummary>("/api/hospitality/gaming-tax"),
  });

  const na = naQ.data && !naQ.data.message ? naQ.data : undefined;
  const t31 = t31Q.data && !t31Q.data.message ? t31Q.data : undefined;
  const gt = gtQ.data && !gtQ.data.message ? gtQ.data : undefined;

  // "—" while loading or when the summary returned an empty/message payload.
  const drjValue = naQ.isLoading ? "—" : na?.drj ? money(na.drj.total_credit, 0) : "—";
  const overShortValue = naQ.isLoading ? "—" : na ? money(na.net_over_short, 0) : "—";
  const ctrCount = t31Q.isLoading ? "—" : t31 ? String(t31.ctrs.length) : "—";
  const accrualValue = gtQ.isLoading ? "—" : gt ? money(gt.today_accrual, 0) : "—";

  const ctrNum = t31?.ctrs.length ?? 0;
  const overShortNum = na ? Number(na.net_over_short) : 0;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Hospitality"
        subtitle="Resorts World Las Vegas"
        right={
          na?.drj ? (
            <StatusPill tone={na.drj.balanced ? "success" : "exception"}>
              {na.drj.balanced ? "Balanced" : "Out of balance"}
            </StatusPill>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        <div className="space-y-4">
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <StatCard
              label="Daily Revenue Journal"
              value={drjValue}
              tone={na?.drj && !na.drj.balanced ? "danger" : "default"}
            />
            <StatCard
              label="Net over/short"
              value={overShortValue}
              tone={!na ? "default" : overShortNum !== 0 ? "warning" : "success"}
            />
            <StatCard
              label="CTRs required"
              value={ctrCount}
              tone={ctrNum > 0 ? "danger" : "default"}
            />
            <StatCard
              label="Today's gaming-tax accrual"
              value={accrualValue}
              tone={gt ? "success" : "default"}
              caption={gt?.marginal_rate ? `Marginal ${gt.marginal_rate}` : undefined}
            />
          </div>

          {/* Module cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <ModuleCard
              to="/hospitality/night-audit"
              icon={<MoonIcon />}
              name="Night Audit"
              description="Balanced daily revenue journal + over/short income audit"
              metric={na?.drj ? money(na.drj.total_credit, 0) : undefined}
            />
            <ModuleCard
              to="/hospitality/title-31"
              icon={<ShieldIcon />}
              name="Title 31"
              description="CTR filings + structuring (SAR) cash-transaction watch"
              metric={t31 ? `${t31.ctrs.length} CTR${t31.ctrs.length === 1 ? "" : "s"}` : undefined}
            />
            <ModuleCard
              to="/hospitality/gaming-tax"
              icon={<CoinIcon />}
              name="Gaming Tax"
              description="Gross gaming revenue + GCB Reg-6 percentage fee"
              metric={gt ? money(gt.today_accrual, 0) : undefined}
            />
            <ModuleCard
              to="/hospitality/revenue-centers"
              icon={<GridIcon />}
              name="Revenue Centers"
              description="Outlets by department and source system"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({
  to,
  icon,
  name,
  description,
  metric,
}: {
  to: string;
  icon: ReactNode;
  name: string;
  description: string;
  metric?: string;
}) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3.5 py-3 flex items-start gap-3 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
    >
      <span className="mt-0.5 text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{name}</span>
          {metric && (
            <span className="ml-auto text-sm font-medium tabular-nums text-slate-700 dark:text-slate-300">
              {metric}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[12.5px] text-slate-500 dark:text-slate-400">{description}</div>
      </div>
    </Link>
  );
}

// ── Inline icons (no icon lib in this app) ─────────────────────────────────
function iconProps() {
  return {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
}
function MoonIcon() {
  return (
    <svg {...iconProps()} aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg {...iconProps()} aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function CoinIcon() {
  return (
    <svg {...iconProps()} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9.5 9.5h3.5a2 2 0 0 1 0 4H10" />
    </svg>
  );
}
function GridIcon() {
  return (
    <svg {...iconProps()} aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
